import { lookupPostalCode } from '@/api/partners';
import { ScanIdCardButton } from '@/components/partners';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useCheckPartnerDuplicate, usePartnerMutations } from '@/hooks/use-partners';
import { validateHuPhone, validateHuPostalCode, validateHuTaxNumber } from '@/lib/hu-validators';
import { useTranslation } from '@kgc/i18n';
import type { ExtractedIdFields, IdCardType } from '@kgc/tesseract-provider';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TransactionIntentCards } from './components/TransactionIntentCards';
import { PARTNER_CATEGORIES, PARTNER_TYPES } from './mock-data';
import type { PartnerFieldKey, TransactionIntent } from './partner-field-matrix';
import { fieldState, missingFields, type PartnerFormSlice } from './partner-field-matrix-adapter';
import type { PartnerCategory, PartnerType } from './types';

interface FormData {
  type: PartnerType;
  categories: PartnerCategory[];
  name: string;
  companyName: string;
  shortName: string;
  taxNumber: string;
  registrationNumber: string;
  email: string;
  notificationEmail: string;
  billingEmail: string;
  billingEmailSame: boolean;
  phone: string;
  website: string;
  // Address
  postalCode: string;
  city: string;
  street: string;
  building: string;
  // Temporary address (FG-1-4)
  temporaryAddress: string;
  tempAddressValidUntil: string;
  // Personal data (individuals)
  birthDate: string;
  birthPlace: string;
  mothersName: string;
  idCardNumber: string;
  // Story 70-1E-a: OCR-prefilled id card metadata
  idCardExpiry: string;
  idCardType: IdCardType | '';
  // Financial
  paymentTermDays: number;
  creditLimit: string;
  discountPercent: string;
  discountRental: string;
  discountSales: string;
  discountService: string;
  rentalDepositPercent: string;
  // Notes
  notes: string;
}

const INITIAL_FORM_DATA: FormData = {
  type: 'company',
  categories: [],
  name: '',
  companyName: '',
  shortName: '',
  taxNumber: '',
  registrationNumber: '',
  email: '',
  notificationEmail: '',
  billingEmail: '',
  billingEmailSame: true,
  phone: '',
  website: '',
  postalCode: '',
  city: '',
  street: '',
  building: '',
  temporaryAddress: '',
  tempAddressValidUntil: '',
  birthDate: '',
  birthPlace: '',
  mothersName: '',
  idCardNumber: '',
  idCardExpiry: '',
  idCardType: '',
  paymentTermDays: 8,
  creditLimit: '',
  discountPercent: '',
  discountRental: '',
  discountSales: '',
  discountService: '',
  rentalDepositPercent: '30',
  notes: '',
};

/** P1 HU-formátum validátorok → mező → validátor-függvény (onBlur + submit-gate). */
const FORMAT_VALIDATORS = {
  taxNumber: validateHuTaxNumber,
  phone: validateHuPhone,
  postalCode: validateHuPostalCode,
} as const;

export function PartnerCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createPartner, isLoading: isSaving, error: saveError } = usePartnerMutations();
  const { duplicates, check: checkDuplicates, clear: clearDuplicates } = useCheckPartnerDuplicate();
  // Story 70-1E-a: show a teal banner after a successful OCR prefill.
  const [scanSuccessBanner, setScanSuccessBanner] = useState(false);
  // P1: pultos "Mentés és új" után visszajelző banner (a form resetel, nem navigál).
  const [savedAndNewBanner, setSavedAndNewBanner] = useState(false);
  // P0 (Zeno #186): inline mező-szintű validáció az alert() helyett (aria-wired).
  const [errors, setErrors] = useState<Record<string, string>>({});
  const firstErrorRef = useRef<string | null>(null);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  /** Q-PR4a: tobb-talalatos irsz (334 ilyen kod) → valaszto; ures = nincs valasztas. */
  const [settlementOptions, setSettlementOptions] = useState<string[]>([]);
  /**
   * 61Y partner-L1: MIT INTÉZ az ügyfél — ez vezérli a mező-mátrixot.
   * `null` = a pultos nem választott → a form a korábbi viselkedését tartja.
   */
  const [intent, setIntent] = useState<TransactionIntent | null>(null);

  /** Az űrlap-állapot azon szelete, amiből a mátrix-kontextus levezethető. */
  const formSlice: PartnerFormSlice = {
    type: formData.type,
    taxNumber: formData.taxNumber,
    discountPercent: formData.discountPercent,
    discountRental: formData.discountRental,
    discountSales: formData.discountSales,
    discountService: formData.discountService,
  };

  /**
   * A mátrix szerint kötelező-e a mező. Intent nélkül MINDIG `false` — a jelölés
   * ilyenkor a korábbi (típus-alapú) logikán marad.
   *
   * ⚠️ A hívó helyeken ez a mai jelöléssel VAGY-kapcsolatban áll: a mátrix csak
   * hozzátehet kötelezőséget, elvenni nem tud. Amíg a `validate()` hard-gate él
   * (magánszemélynél anyja neve + személyi), addig egy mátrix-alapú „nem kötelező"
   * olyan mezőt hagyna jelöletlenül, amin a mentés némán elbukik.
   */
  const isMatrixRequired = (field: PartnerFieldKey): boolean =>
    intent !== null && fieldState(field, formSlice, intent).status === 'required';

  /** Cég-partnernél értelmetlen mezők (anyja neve, személyi, születési adatok). */
  const isSuppressed = (field: PartnerFieldKey): boolean =>
    intent !== null && fieldState(field, formSlice, intent).status === 'not-applicable';

  /**
   * Soft-enforce (kliens-ratify 2026-07-28): a hiányzó kötelező mezőket JELÖLJÜK,
   * de a mentést nem blokkoljuk.
   *
   * ⚠️ Az `addressCard` és a `representative` `true`-val szerepel: nincs hozzájuk
   * űrlap-mező, így „hiányzó"-ként listázni őket olyan pótlást kérne a pultostól,
   * amit nem tud elvégezni. A lakcímkártya valós kliens-igény (FK-PART-4/6) — a
   * mező FE+BE+séma-oldali felvitele külön tétel.
   */
  const filledFields: Partial<Record<PartnerFieldKey, boolean>> = {
    name: formData.name.trim() !== '',
    phone: formData.phone.trim() !== '',
    email: formData.email.trim() !== '',
    address:
      formData.postalCode.trim() !== '' &&
      formData.city.trim() !== '' &&
      formData.street.trim() !== '',
    idCard: formData.idCardNumber.trim() !== '',
    birthData: formData.birthDate.trim() !== '' && formData.birthPlace.trim() !== '',
    mothersName: formData.mothersName.trim() !== '',
    taxNumber: formData.taxNumber.trim() !== '',
    addressCard: true,
    representative: true,
  };

  const missingRequired: PartnerFieldKey[] =
    intent === null ? [] : missingFields(formSlice, intent, filledFields);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Story 70-1E-a: handle OCR-extracted fields by prefilling the personal-data
   * section of the form. Surname + givenNames → `name`; idCardNumber, expiry,
   * type → corresponding fields. Empty OCR fields DO NOT clear existing input.
   */
  const handleScanned = (fields: ExtractedIdFields): void => {
    setFormData(prev => {
      const next = { ...prev };
      // Combine surname + givenNames only if we actually parsed them — never wipe manual entry.
      if (fields.surname || fields.givenNames) {
        const composed = [fields.surname, fields.givenNames].filter(Boolean).join(' ').trim();
        if (composed.length > 0) next.name = composed;
      }
      if (fields.idCardNumber) next.idCardNumber = fields.idCardNumber;
      if (fields.idCardExpiry) next.idCardExpiry = fields.idCardExpiry;
      if (fields.idCardType) next.idCardType = fields.idCardType;
      if (fields.dateOfBirth) next.birthDate = fields.dateOfBirth;
      // OCR will not fill mothersName (not on MRZ) — leave alone.
      return next;
    });
    setScanSuccessBanner(true);
  };

  const toggleCategory = (category: PartnerCategory) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category],
    }));
  };

  // P0 (Zeno #186 finding): inline aria-validáció az `alert()` helyett.
  // Visszaadja a hibatérképet + a fókuszálandó első mezőt (focus-first-error a NN/g szerint).
  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!formData.name.trim()) next.name = t('partner.nameRequired');
    if (formData.categories.length === 0) next.categories = t('partner.categoryRequired');
    // Az átvevő (magánszemély) személyes adatai kötelezők a gép-kiadáshoz.
    if (formData.type === 'individual') {
      if (!formData.mothersName.trim()) next.mothersName = t('partner.mothersNameRequired');
      if (!formData.idCardNumber.trim()) next.idCardNumber = t('partner.idCardRequired');
    }
    // P1: HU-formátum (adószám/telefon/irsz) — submit-gate, hogy a hibás formátum
    // ne csússzon át (a mezők opcionálisak → üresen valid).
    (Object.keys(FORMAT_VALIDATORS) as Array<keyof typeof FORMAT_VALIDATORS>).forEach(field => {
      const res = FORMAT_VALIDATORS[field](formData[field]);
      if (!res.valid && res.errorKey) next[field] = t(res.errorKey);
    });
    return next;
  };

  // Egy mező hibájának törlése amint a user javítja ("reward early" — Baymard).
  const clearError = (field: string) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const rest = { ...prev };
      delete rest[field];
      return rest;
    });
  };

  // P1: HU-formátum onBlur-validáció — a mező elhagyásakor jelzünk ("punish late"),
  // a `clearError` az onChange-en már törli a hibát ("reward early").
  /**
   * Q-PR4a: irányítószám-blur → település auto-kitöltés (Zsuzsi-igény, 07-27).
   *
   * A wire MÉRVE dev4-en (lásd `api/partners.ts lookupPostalCode` doc-blokk):
   *  - ismeretlen/érvénytelen kód → **200 + üres tömb**, NEM 404 → itt CSENDES üres-ág
   *    (a blur félgépelt kódra nem villoghat hibát), a formátum-hibát a
   *    `validateFormatOnBlur` külön kezeli
   *  - 1 találat → auto-kitöltés, DE csak ha a helység MÉG ÜRES (a kézi gépelést
   *    sosem írjuk felül — a `handleScanned` OCR-ág is ezt az elvet követi)
   *  - több találat (334 ilyen kód, pl. 2066 → [Szár, Újbarok]) → választó,
   *    nem néma első-elem
   */
  const lookupSettlement = async (code: string) => {
    const normalized = code.trim();
    if (!/^\d{4}$/.test(normalized)) {
      setSettlementOptions([]);
      return;
    }
    try {
      const { settlements } = await lookupPostalCode(normalized);
      if (settlements.length === 1) {
        setSettlementOptions([]);
        setFormData(prev => (prev.city.trim() ? prev : { ...prev, city: settlements[0] ?? '' }));
        return;
      }
      setSettlementOptions(settlements);
    } catch {
      // A lookup SOHA nem törheti meg a form-kitöltést (nemzeti referencia-adat,
      // nem üzleti gate) — hálózati hiba esetén a mező marad kézi kitöltésre.
      setSettlementOptions([]);
    }
  };

  const validateFormatOnBlur = (field: keyof typeof FORMAT_VALIDATORS) => {
    const res = FORMAT_VALIDATORS[field](formData[field]);
    if (res.valid) {
      clearError(field);
      // Normalizált érték visszaírása a mezőbe (pl. telefon "06 30…" → E.164 "+3630…"),
      // hogy a tárolt+látható érték konzisztens legyen (dedup/SMS-gateway).
      if (res.normalized && res.normalized !== formData[field]) {
        updateField(field, res.normalized);
      }
      return;
    }
    setErrors(prev => ({ ...prev, [field]: t(res.errorKey ?? '') }));
  };

  // P0 dedup (Allow-with-Alert): név/adószám blur-re non-blocking duplikátum-check.
  // A hook (useCheckPartnerDuplicate) maga gate-eli a min-hosszt (név≥2 / adószám≥4).
  const runDedupCheck = () => {
    void checkDuplicates({
      name: formData.name.trim() || undefined,
      taxNumber: formData.taxNumber.trim() || undefined,
    });
  };

  const handleSubmit = async (e: React.FormEvent, opts: { stayOnPage?: boolean } = {}) => {
    e.preventDefault();
    setSavedAndNewBanner(false);

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      // focus-first-error: az első hibás mezőre ugrunk (DOM-sorrend szerint).
      const order = [
        'categories',
        'name',
        'mothersName',
        'idCardNumber',
        'taxNumber',
        'phone',
        'postalCode',
      ];
      firstErrorRef.current = order.find(k => found[k]) ?? Object.keys(found)[0] ?? null;
      const el = firstErrorRef.current
        ? document.querySelector<HTMLElement>(`[data-error-field="${firstErrorRef.current}"]`)
        : null;
      el?.focus();
      return;
    }
    setErrors({});

    // Partner P0 persist (#188 óta a BE maszkolva tárolja az idCardNumber-t).
    const address = [formData.street, formData.building]
      .map(s => s.trim())
      .filter(Boolean)
      .join(' ');
    // Telefon E.164-normalizálás a submitnél is (Ctrl+Enter blur nélkül is normalizál);
    // a submit-gate garantálja hogy itt a phone üres VAGY valid → a normalized létezik.
    const phoneTrimmed = formData.phone.trim();
    const phoneNormalized = phoneTrimmed
      ? (validateHuPhone(phoneTrimmed).normalized ?? phoneTrimmed)
      : '';
    try {
      const res = await createPartner({
        type: formData.type === 'company' ? 'COMPANY' : 'INDIVIDUAL',
        name: formData.name,
        // Q-T137 #3: cégnév — céges típusnál a főnév, magánszemélynél a külön
        // opcionális companyName mező (magánszemély is felvihető céggel; a BE :663
        // create fogadja individual-módban is, üres → null).
        ...(formData.type === 'company'
          ? { companyName: formData.name }
          : formData.companyName.trim()
            ? { companyName: formData.companyName.trim() }
            : {}),
        ...(formData.taxNumber.trim() ? { taxNumber: formData.taxNumber.trim() } : {}),
        ...(formData.email.trim() ? { email: formData.email.trim() } : {}),
        ...(phoneNormalized ? { phone: phoneNormalized } : {}),
        ...(formData.postalCode.trim() ? { postalCode: formData.postalCode.trim() } : {}),
        ...(formData.city.trim() ? { city: formData.city.trim() } : {}),
        ...(address ? { address } : {}),
        // Átvevő ID-adatok — #188: a BE az idCardNumber-t maszkolva (last-4) tárolja,
        // a mothersName/birth*/idCardExpiry nyersen (szerződés-szükséges, 6(1)(b)).
        ...(formData.mothersName.trim() ? { mothersName: formData.mothersName.trim() } : {}),
        ...(formData.birthDate.trim() ? { birthDate: formData.birthDate.trim() } : {}),
        ...(formData.birthPlace.trim() ? { birthPlace: formData.birthPlace.trim() } : {}),
        ...(formData.idCardNumber.trim() ? { idCardNumber: formData.idCardNumber.trim() } : {}),
        ...(formData.idCardExpiry.trim() ? { idCardExpiry: formData.idCardExpiry.trim() } : {}),
        paymentTermDays: formData.paymentTermDays,
        ...(formData.creditLimit.trim() ? { creditLimit: Number(formData.creditLimit) } : {}),
        // Q-T031 create-pár: a kedvezmény-mezők bekötése (eddig némán elvesztek a create-en;
        // az edit-path már mappelte). Üres → kihagyva (a BE 0/null-ra defaultol).
        ...(formData.discountPercent.trim()
          ? { defaultDiscountPc: Number(formData.discountPercent) }
          : {}),
        ...(formData.discountRental.trim()
          ? { discountRental: Number(formData.discountRental) }
          : {}),
        ...(formData.discountSales.trim() ? { discountSales: Number(formData.discountSales) } : {}),
        ...(formData.discountService.trim()
          ? { discountService: Number(formData.discountService) }
          : {}),
        // Q-T137 #1: partner-kategóriák — a create-payload eddig NEM küldte (a BE type-ból
        // inferálta); a #957 óta a body.categories perzisztál → a form-értéket bekötjük
        // (min 1 kötelező a validate-ben, ezért mindig van legalább 1 elem).
        categories: formData.categories,
        ...(formData.notes.trim() ? { notes: formData.notes } : {}),
      });
      if (opts.stayOnPage) {
        // Pultos "Mentés és új": reset + visszajelzés, ugyanazon az oldalon maradunk.
        setFormData(INITIAL_FORM_DATA);
        setScanSuccessBanner(false);
        clearDuplicates();
        setSavedAndNewBanner(true);
        const nameField = document.querySelector<HTMLElement>('[data-error-field="name"]');
        nameField?.focus();
        return;
      }
      navigate(`/partners/${res.data.id}`);
    } catch {
      // a hiba a hookban setError-rel kezelve → a saveError banner jeleníti meg
    }
  };

  // Pultos "Mentés és új" — ugyanaz a submit-út, de a sikeres mentés után marad + resetel.
  const handleSaveAndNew = (e: React.FormEvent) => {
    void handleSubmit(e, { stayOnPage: true });
  };

  // Ctrl/⌘+Enter = gyors mentés (pultos billentyűzet-workflow).
  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      void handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/partners')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('partner.createPartner')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('partner.registerData')}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90"
          >
            {t('common.save')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* P1: "Mentés és új" visszajelzés — a partner mentve, a form üres az új rögzítéshez. */}
        {savedAndNewBanner && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 rounded-md border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800"
            data-testid="partner-create-saved-banner"
          >
            {t('partner.saveAndNewSuccess')}
          </div>
        )}
        {/* P0 (Allow-with-Alert): non-blocking duplikátum-figyelmeztetés — NEM tiltja a mentést. */}
        {duplicates.length > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid="partner-create-dup-warning"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{t('partner.duplicate.warning')}</p>
                <ul className="mt-1 list-disc pl-5">
                  {duplicates.slice(0, 5).map(d => (
                    <li key={d.id}>
                      <button
                        type="button"
                        className="underline hover:no-underline"
                        onClick={() => navigate(`/partners/${d.id}`)}
                      >
                        {d.name}
                        {d.taxNumber ? ` · ${d.taxNumber}` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                className="shrink-0 text-amber-700 underline hover:no-underline"
                onClick={clearDuplicates}
              >
                {t('common.dismiss')}
              </button>
            </div>
          </div>
        )}
        {/* 61Y partner-L1 soft-enforce: a hiányzó kötelező mezők JELÖLVE — a mentés
            NEM blokkolt (kliens-ratify 2026-07-28). Csak választott ügytípusnál jelenik meg. */}
        {missingRequired.length > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid="partner-create-missing-required"
          >
            <p className="font-medium">{t('partner.requiredMissing.title')}</p>
            <ul className="mt-1 list-disc pl-5">
              {missingRequired.map(field => (
                <li key={field}>{t(`partner.field.${field}`)}</li>
              ))}
            </ul>
            <p className="mt-1">{t('partner.requiredMissing.hint')}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-6">
          {/* 61Y partner-L1: belépő-kérdés — mit intéz az ügyfél? A választás vezérli,
              mely mezőket kérjük; elhagyható (ilyenkor a korábbi viselkedés marad). */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.intent.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <TransactionIntentCards value={intent} onChange={setIntent} />
            </CardContent>
          </Card>

          {/* Type & Categories */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.typeAndCategory')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.partnerType')} *
                  </label>
                  <div className="flex gap-4">
                    {PARTNER_TYPES.map(type => (
                      <label key={type.value} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name="type"
                          value={type.value}
                          checked={formData.type === type.value}
                          onChange={e => updateField('type', e.target.value as PartnerType)}
                          className="h-4 w-4"
                        />
                        <span>{t(type.labelKey)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.categoriesRequired')} *
                  </label>
                  <div className="flex flex-wrap gap-2" data-error-field="categories" tabIndex={-1}>
                    {PARTNER_CATEGORIES.map(cat => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => {
                          toggleCategory(cat.value);
                          clearError('categories');
                        }}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                          formData.categories.includes(cat.value)
                            ? cat.color
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#1f5f6b] dark:text-[#ccfbf1] dark:hover:bg-[#0f3d47]'
                        }`}
                      >
                        {t(cat.labelKey)}
                      </button>
                    ))}
                  </div>
                  {errors.categories && (
                    <p role="alert" className="mt-1 text-sm text-red-600">
                      {errors.categories}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Átvevő (magánszemély) — ELSŐDLEGES kötelező blokk (Partner P0 fit-gap):
              a gép-kiadáshoz az átvevő személyes adatai elengedhetetlenek → ne lehessen átugorni.
              61Y partner-L1: cég-partnernél VÁLASZTOTT ügytípus mellett a blokk minden mezője
              `not-applicable` (egy Kft.-nek nincs anyja neve és személyije) → ilyenkor elrejtjük.
              Ügytípus nélkül a blokk változatlanul MINDIG látszik. */}
          {!(
            isSuppressed('idCard') &&
            isSuppressed('mothersName') &&
            isSuppressed('birthData')
          ) && (
            <Card>
              <CardHeader>
                <CardTitle>{t('partner.receiverData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-muted-foreground">{t('partner.receiverHint')}</p>

                {/* Story 70-1E-a: ID card scan trigger + success banner */}
                <div className="mb-4 flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {t('partner.scan.buttonAriaLabel')}
                  </p>
                  <ScanIdCardButton
                    onScanned={handleScanned}
                    data-testid="partner-create-scan-id"
                  />
                </div>
                {scanSuccessBanner && (
                  <div
                    role="status"
                    className="mb-4 rounded-none border-2 p-3 text-sm"
                    style={{
                      borderColor: '#14B8A6',
                      backgroundColor: 'rgba(20, 184, 166, 0.08)',
                      color: '#0d7d6b',
                    }}
                    data-testid="partner-create-scan-success"
                  >
                    {t('partner.scan.success')}
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  {formData.type === 'individual' && (
                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-sm font-medium">
                        {t('partner.fullName')} *
                      </label>
                      <Input
                        value={formData.name}
                        onChange={e => {
                          updateField('name', e.target.value);
                          clearError('name');
                        }}
                        onBlur={runDedupCheck}
                        placeholder={t('partner.placeholder.personName')}
                        required
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'err-name' : undefined}
                        data-error-field="name"
                        data-testid="partner-create-name"
                      />
                      {errors.name && (
                        <p id="err-name" role="alert" className="mt-1 text-sm text-red-600">
                          {errors.name}
                        </p>
                      )}
                    </div>
                  )}
                  {formData.type === 'individual' && (
                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-sm font-medium">
                        {t('partner.companyNameOptional')}
                      </label>
                      <Input
                        value={formData.companyName}
                        onChange={e => updateField('companyName', e.target.value)}
                        placeholder={t('partner.placeholder.companyName')}
                        data-testid="partner-create-company-name-individual"
                      />
                    </div>
                  )}
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.birthDate')}
                      {isMatrixRequired('birthData') ? ' *' : ''}
                    </label>
                    <Input
                      type="date"
                      value={formData.birthDate}
                      onChange={e => updateField('birthDate', e.target.value)}
                      aria-required={isMatrixRequired('birthData') || undefined}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.birthPlace')}
                      {isMatrixRequired('birthData') ? ' *' : ''}
                    </label>
                    <Input
                      value={formData.birthPlace}
                      onChange={e => updateField('birthPlace', e.target.value)}
                      placeholder={t('partner.placeholder.birthPlace')}
                      aria-required={isMatrixRequired('birthData') || undefined}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.mothersName')}
                      {formData.type === 'individual' || isMatrixRequired('mothersName')
                        ? ' *'
                        : ''}
                    </label>
                    <Input
                      value={formData.mothersName}
                      onChange={e => {
                        updateField('mothersName', e.target.value);
                        clearError('mothersName');
                      }}
                      placeholder={t('partner.placeholder.mothersName')}
                      required={formData.type === 'individual' || isMatrixRequired('mothersName')}
                      aria-invalid={errors.mothersName ? true : undefined}
                      aria-describedby={errors.mothersName ? 'err-mothers' : undefined}
                      data-error-field="mothersName"
                      data-testid="partner-create-mothers-name"
                    />
                    {errors.mothersName && (
                      <p id="err-mothers" role="alert" className="mt-1 text-sm text-red-600">
                        {errors.mothersName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.idCardNumber')}
                      {formData.type === 'individual' || isMatrixRequired('idCard') ? ' *' : ''}
                    </label>
                    <Input
                      value={formData.idCardNumber}
                      onChange={e => {
                        updateField('idCardNumber', e.target.value);
                        clearError('idCardNumber');
                      }}
                      placeholder={t('partner.placeholder.idCardNumber')}
                      required={formData.type === 'individual' || isMatrixRequired('idCard')}
                      aria-invalid={errors.idCardNumber ? true : undefined}
                      aria-describedby={errors.idCardNumber ? 'err-idcard' : undefined}
                      data-error-field="idCardNumber"
                      data-testid="partner-create-id-number"
                    />
                    {errors.idCardNumber && (
                      <p id="err-idcard" role="alert" className="mt-1 text-sm text-red-600">
                        {errors.idCardNumber}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.idCardExpiry')}
                    </label>
                    <Input
                      type="date"
                      value={formData.idCardExpiry}
                      onChange={e => updateField('idCardExpiry', e.target.value)}
                      data-testid="partner-create-id-expiry"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Céges adatok (MÁSODLAGOS, költségviselő) — csak céges típusnál. */}
          {formData.type === 'company' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('partner.companyDataSecondary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.companyName')} *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={e => {
                        updateField('name', e.target.value);
                        clearError('name');
                      }}
                      onBlur={runDedupCheck}
                      placeholder={t('partner.placeholder.companyName')}
                      required
                      aria-invalid={errors.name ? true : undefined}
                      aria-describedby={errors.name ? 'err-name-company' : undefined}
                      data-error-field="name"
                      data-testid="partner-create-company-name"
                    />
                    {errors.name && (
                      <p id="err-name-company" role="alert" className="mt-1 text-sm text-red-600">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.shortName')}
                    </label>
                    <Input
                      value={formData.shortName}
                      onChange={e => updateField('shortName', e.target.value)}
                      placeholder={t('partner.placeholder.lastName')}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.taxNumber')}
                      {isMatrixRequired('taxNumber') ? ' *' : ''}
                    </label>
                    <Input
                      aria-required={isMatrixRequired('taxNumber') || undefined}
                      value={formData.taxNumber}
                      onChange={e => {
                        updateField('taxNumber', e.target.value);
                        clearError('taxNumber');
                      }}
                      onBlur={() => {
                        validateFormatOnBlur('taxNumber');
                        runDedupCheck();
                      }}
                      placeholder="12345678-2-42"
                      aria-invalid={errors.taxNumber ? true : undefined}
                      aria-describedby={errors.taxNumber ? 'err-tax' : undefined}
                      data-error-field="taxNumber"
                      data-testid="partner-create-tax-number"
                    />
                    {errors.taxNumber && (
                      <p id="err-tax" role="alert" className="mt-1 text-sm text-red-600">
                        {errors.taxNumber}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.registrationNumber')}
                    </label>
                    <Input
                      value={formData.registrationNumber}
                      onChange={e => updateField('registrationNumber', e.target.value)}
                      placeholder="01-09-123456"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.contactInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.email')}
                    {isMatrixRequired('email') ? ' *' : ''}
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => updateField('email', e.target.value)}
                    placeholder={t('partner.placeholder.email')}
                    aria-required={isMatrixRequired('email') || undefined}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.notificationEmail')}
                  </label>
                  <Input
                    type="email"
                    value={formData.notificationEmail}
                    onChange={e => updateField('notificationEmail', e.target.value)}
                    placeholder={t('partner.placeholder.email')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.billingEmail')}
                  </label>
                  <Input
                    type="email"
                    value={
                      formData.billingEmailSame ? formData.notificationEmail : formData.billingEmail
                    }
                    onChange={e => updateField('billingEmail', e.target.value)}
                    placeholder={t('partner.placeholder.email')}
                    disabled={formData.billingEmailSame}
                  />
                  <label className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={formData.billingEmailSame}
                      onChange={e => updateField('billingEmailSame', e.target.checked)}
                      className="rounded border-input"
                    />
                    {t('partner.billingEmailSameAsNotification')}
                  </label>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.phone')}
                    {isMatrixRequired('phone') ? ' *' : ''}
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={e => {
                      updateField('phone', e.target.value);
                      clearError('phone');
                    }}
                    onBlur={() => validateFormatOnBlur('phone')}
                    placeholder="+36 30 123 4567"
                    aria-required={isMatrixRequired('phone') || undefined}
                    aria-invalid={errors.phone ? true : undefined}
                    aria-describedby={errors.phone ? 'err-phone' : undefined}
                    data-error-field="phone"
                    data-testid="partner-create-phone"
                  />
                  {errors.phone && (
                    <p id="err-phone" role="alert" className="mt-1 text-sm text-red-600">
                      {errors.phone}
                    </p>
                  )}
                </div>
                {formData.type === 'company' && (
                  <div>
                    <label className="mb-2 block text-sm font-medium">{t('partner.website')}</label>
                    <Input
                      value={formData.website}
                      onChange={e => updateField('website', e.target.value)}
                      placeholder={t('partner.placeholder.website')}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.billingAddress')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.postalCode')}
                    {isMatrixRequired('address') ? ' *' : ''}
                  </label>
                  <Input
                    value={formData.postalCode}
                    onChange={e => {
                      updateField('postalCode', e.target.value);
                      clearError('postalCode');
                    }}
                    onBlur={() => {
                      validateFormatOnBlur('postalCode');
                      void lookupSettlement(formData.postalCode);
                    }}
                    placeholder="1134"
                    aria-invalid={errors.postalCode ? true : undefined}
                    aria-describedby={errors.postalCode ? 'err-postal' : undefined}
                    data-error-field="postalCode"
                    data-testid="partner-create-postal-code"
                  />
                  {errors.postalCode && (
                    <p id="err-postal" role="alert" className="mt-1 text-sm text-red-600">
                      {errors.postalCode}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <label htmlFor="partner-city" className="mb-2 block text-sm font-medium">
                    {t('partner.city')}
                    {isMatrixRequired('address') ? ' *' : ''}
                  </label>
                  <Input
                    aria-required={isMatrixRequired('address') || undefined}
                    id="partner-city"
                    value={formData.city}
                    onChange={e => {
                      updateField('city', e.target.value);
                      setSettlementOptions([]);
                    }}
                    placeholder={t('partner.placeholder.city')}
                    data-testid="partner-create-city"
                  />
                  {/* Q-PR4a: tobb telepules ugyanazon az irsz-en (334 ilyen kod, pl. 2066)
                      → valasztat, nem nema elso-elem. A valasztas utan a lista eltunik. */}
                  {settlementOptions.length > 1 && (
                    <div className="mt-2" data-testid="partner-settlement-options">
                      <p className="mb-1 text-sm text-muted-foreground">
                        {t('partner.selectSettlement')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {settlementOptions.map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => {
                              updateField('city', s);
                              setSettlementOptions([]);
                            }}
                            className="rounded border border-input bg-background px-3 py-1 text-sm hover:bg-accent hover:text-accent-foreground"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.streetAddress')}
                    {isMatrixRequired('address') ? ' *' : ''}
                  </label>
                  <Input
                    value={formData.street}
                    onChange={e => updateField('street', e.target.value)}
                    placeholder={t('partner.placeholder.address')}
                    aria-required={isMatrixRequired('address') || undefined}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">{t('partner.floorDoor')}</label>
                  <Input
                    value={formData.building}
                    onChange={e => updateField('building', e.target.value)}
                    placeholder="3. em."
                  />
                </div>
              </div>

              {/* Temporary address - collapsible (FG-1-4) */}
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  {t('partner.temporaryAddressSection')}
                </summary>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.temporaryAddress')}
                    </label>
                    <Input
                      value={formData.temporaryAddress}
                      onChange={e => updateField('temporaryAddress', e.target.value)}
                      placeholder={t('partner.placeholder.temporaryAddress')}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.tempAddressValidUntil')}
                    </label>
                    <Input
                      type="date"
                      value={formData.tempAddressValidUntil}
                      onChange={e => updateField('tempAddressValidUntil', e.target.value)}
                    />
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>

          {/* Financial */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.financialSettings')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.paymentTermDays')}
                  </label>
                  <Input
                    type="number"
                    value={formData.paymentTermDays}
                    onChange={e => updateField('paymentTermDays', Number(e.target.value))}
                    min={0}
                    max={90}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.creditLimitFt')}
                  </label>
                  <Input
                    type="number"
                    value={formData.creditLimit}
                    onChange={e => updateField('creditLimit', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.discountPercent')}
                  </label>
                  <Input
                    type="number"
                    data-testid="partner-create-discount-percent"
                    value={formData.discountPercent}
                    onChange={e => updateField('discountPercent', e.target.value)}
                    placeholder="0"
                    min={0}
                    max={50}
                  />
                </div>
              </div>

              {/* Kedvezmény kategorizálás (O-PRT-1) */}
              <div className="mt-6 border-t pt-4 dark:border-[#2d7d8a]">
                <h4 className="mb-1 text-sm font-semibold text-gray-700 dark:text-[#ccfbf1]">
                  {t('partner.discount.categorized')}
                </h4>
                <p className="mb-3 text-xs text-gray-500 dark:text-[#99f6e4]">
                  {t('partner.discount.max')} &mdash; {t('partner.discount.noStacking')}
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.discount.rental')}
                    </label>
                    <Input
                      type="number"
                      data-testid="partner-create-discount-rental"
                      value={formData.discountRental}
                      onChange={e => updateField('discountRental', e.target.value)}
                      placeholder="0"
                      min={0}
                      max={15}
                      step={1}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.discount.sales')}
                    </label>
                    <Input
                      type="number"
                      data-testid="partner-create-discount-sales"
                      value={formData.discountSales}
                      onChange={e => updateField('discountSales', e.target.value)}
                      placeholder="0"
                      min={0}
                      max={15}
                      step={1}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.discount.service')}
                    </label>
                    <Input
                      type="number"
                      data-testid="partner-create-discount-service"
                      value={formData.discountService}
                      onChange={e => updateField('discountService', e.target.value)}
                      placeholder="0"
                      min={0}
                      max={15}
                      step={1}
                    />
                  </div>
                </div>
              </div>

              {formData.categories.includes('rental') && (
                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.rentalDepositPercent')}
                  </label>
                  <Input
                    type="number"
                    value={formData.rentalDepositPercent}
                    onChange={e => updateField('rentalDepositPercent', e.target.value)}
                    min={0}
                    max={100}
                    className="max-w-xs"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('common.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                className="w-full rounded-md border p-3 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
                rows={4}
                placeholder={t('partner.notesPlaceholder')}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          {saveError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {saveError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="mr-auto text-xs text-muted-foreground">{t('partner.saveHint')}</span>
            <Button type="button" variant="outline" onClick={() => navigate('/partners')}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={handleSaveAndNew}
              data-testid="partner-create-save-and-new"
            >
              {t('partner.saveAndNew')}
            </Button>
            <Button type="submit" disabled={isSaving} className="bg-primary hover:bg-primary/90">
              {t('partner.createPartner')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
